import { useLayoutEffect, useRef, type ReactNode } from "react";

import { DateDivider } from "./DateDivider";
import { MessageAiCallCard } from "./MessageAiCallCard";
import { MessageCallCard } from "./MessageCallCard";
import { SenderGroupView } from "./SenderGroupView";
import { Column, Text } from "@/components";

import {
  groupBySender,
  groupTimelineByDate,
  QuoTimelineItemKind,
  type QuoTimelineItem,
} from "@/lib/quoMessages";
import {
  CallHistoryAiCall,
  CallHistoryStafferCall,
  QuoMessageResponse,
} from "@/types";

export const MessagesList = ({
  aiCalls = [],
  calls = [],
  candidateFirstName = "Candidate",
  footer,
  messages,
}: {
  aiCalls?: CallHistoryAiCall[];
  calls?: CallHistoryStafferCall[];
  candidateFirstName?: string;
  // Pinned to the end of the thread, inside the scroll container, so it scrolls
  // away with the conversation instead of hovering above the composer.
  footer?: ReactNode;
  messages: QuoMessageResponse[];
}) => {
  /* React Hooks */
  const messagesListRef = useRef<HTMLDivElement>(null);
  const hasScrolledToLatestMessage = useRef(false);
  const hasScrolledForFooter = useRef(false);
  const hasFooter = !!footer;

  useLayoutEffect(() => {
    // The footer's queued send comes from its own query and lands after the
    // thread has already rendered and scrolled, so it gets one scroll of its
    // own. Both latches together cap this at two, leaving a staffer who has
    // scrolled into history alone once the thread has settled.
    const isFirstScroll = !hasScrolledToLatestMessage.current;
    const isFooterScroll = hasFooter && !hasScrolledForFooter.current;
    if (!isFirstScroll && !isFooterScroll) return;
    if (
      !hasFooter &&
      messages.length === 0 &&
      calls.length === 0 &&
      aiCalls.length === 0
    ) {
      return;
    }

    const messagesList = messagesListRef.current;
    if (!messagesList) return;

    messagesList.scrollTop = messagesList.scrollHeight;
    hasScrolledToLatestMessage.current = true;
    if (hasFooter) hasScrolledForFooter.current = true;
  }, [messages.length, calls.length, aiCalls.length, hasFooter]);

  /* Render */
  // Runs of consecutive messages keep their sender grouping; a call breaks the
  // run and renders as a card in chronological position.
  const renderDateGroupItems = (items: QuoTimelineItem[]) => {
    const rendered: ReactNode[] = [];
    let messageRun: QuoMessageResponse[] = [];

    const flushMessageRun = () => {
      if (messageRun.length === 0) return;
      groupBySender(messageRun).forEach((senderGroup) => {
        rendered.push(
          <SenderGroupView
            group={senderGroup}
            key={senderGroup.messages[0].id}
          />,
        );
      });
      messageRun = [];
    };

    for (const item of items) {
      if (item.kind === QuoTimelineItemKind.MESSAGE) {
        messageRun.push(item.message);
      } else if (item.kind === QuoTimelineItemKind.CALL) {
        flushMessageRun();
        rendered.push(
          <MessageCallCard
            call={item.call}
            candidateFirstName={candidateFirstName}
            key={item.call.id}
          />,
        );
      } else {
        flushMessageRun();
        rendered.push(
          <MessageAiCallCard
            call={item.aiCall}
            candidateFirstName={candidateFirstName}
            key={item.aiCall.id}
          />,
        );
      }
    }
    flushMessageRun();

    return rendered;
  };

  const render = () => {
    if (messages.length === 0 && calls.length === 0 && aiCalls.length === 0) {
      return (
        <Column className="flex-1 items-center justify-center">
          <Text className="text-center text-sm font-normal italic text-slate-500">
            &quot;A quiet nest, no chirps, no cheeps.
            <br />
            The birds have flown, silence creeps.
            <br />
            Go wrangle up a feathered friend, and
            <br />
            let the empty nesting end!&quot;
          </Text>
        </Column>
      );
    }

    const dateGroups = groupTimelineByDate(messages, calls, aiCalls);

    return (
      <>
        {dateGroups.map((dateGroup) => (
          <Column key={dateGroup.date} className="gap-3">
            <DateDivider date={dateGroup.date} />
            {renderDateGroupItems(dateGroup.items)}
          </Column>
        ))}
      </>
    );
  };

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overflow-x-hidden pr-2 [scrollbar-gutter:stable]"
      ref={messagesListRef}
    >
      {render()}
      {footer}
    </div>
  );
};
